import { cn } from "../lib/cn.ts";
import type { HTMLProps } from "../lib/props.ts";
import type { ReactNode } from "react";

export interface LabelProps extends HTMLProps<"label"> {
  /** Appends the conventional required marker, hidden from screen readers. */
  required?: boolean;
}

export function Label({
  required,
  class: klass,
  className,
  children,
  ...props
}: LabelProps) {
  return (
    <label
      className={cn(
        "dd-field-label text-sm font-medium leading-none text-foreground",
        klass,
        className,
      )}
      {...props}
    >
      {children}
      {required && (
        <span aria-hidden="true" className="ml-0.5 text-danger">
          *
        </span>
      )}
    </label>
  );
}

export interface FieldProps extends HTMLProps<"div"> {
  label: ReactNode;
  htmlFor: string;
  required?: boolean;
  /** Error text. When present the field is described by it via aria. */
  error?: string;
  hint?: string;
}

/** Label + control + hint/error, wired together with the right aria plumbing. */
export function Field({
  label,
  htmlFor,
  required,
  error,
  hint,
  class: klass,
  className,
  children,
  ...props
}: FieldProps) {
  return (
    <div
      className={cn("dd-field flex flex-col gap-1.5", klass, className)}
      {...props}
    >
      <Label htmlFor={htmlFor} required={required}>
        {label}
      </Label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
